/**
 * Every gate's pending intent, keyed to the tab rather than to a member.
 *
 * That is what these need to be to do their job — carry an errand across a
 * social round-trip, which leaves and re-enters the page — and also why they
 * have to be swept on the way out.
 *
 * Spelled out here rather than imported from the four modules that own them.
 * This runs from `useLogout`, which is mounted almost everywhere, and importing
 * the owners would pull all four gate state modules into that graph and break
 * any test that mocks one of them. `clear-gate-state.spec.ts` imports both
 * sides and fails if a key ever drifts from its atom.
 */
const GATE_STORAGE_KEYS = [
    'save-gate-intent',
    'save-gate-open',
    'download-gate-intent',
    'download-gate-open',
    'bulk-enquiry-selection',
    'bulk-enquiry-mode',
    'book-meeting-gate-intent',
    'book-meeting-gate-open',
] as const;

/**
 * Forget every gate's pending intent.
 *
 * A gate left open holds what the reader was trying to do: a pin waiting for a
 * board, a file waiting for a download, a Get Matched brief, a bulk selection.
 * Logging out left all of it in the tab, so the next person to sign in on a
 * shared device resumed the last person's errand under their own account —
 * their pin saved to the new member's board, their brief submitted under the
 * new member's email. The Get Matched brief has a 30-minute expiry, which
 * narrows that window without closing it; the rest had none.
 *
 * Called alongside `clearEnquiryDraft` on logout, where the reader has said in
 * as many words that they are done.
 */
export const clearGateState = (): void => {
    if (typeof window === 'undefined') return;
    for (const key of GATE_STORAGE_KEYS) {
        try {
            window.sessionStorage.removeItem(key);
        } catch {
            // Storage blocked or full — there was nothing readable to clear.
        }
    }
};

/** Exported for the drift test only. */
export const GATE_STORAGE_KEYS_FOR_TEST = GATE_STORAGE_KEYS;
